import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown } from 'lucide-react'
import { LOCALE } from '../i18n'

/**
 * Seletor de língua com links ESTÁTICOS — sem deteção, sem cookies.
 * Cada língua é um build separado servido num caminho próprio:
 *   pt → /   ·   en → /en/   ·   uk → /uk/   ·   …
 * Os nomes apresentam-se sempre na língua nativa (não se traduzem).
 *
 * No botão da navbar mostra-se só o globo + o código ("PT"): o nome por
 * extenso não cabe ao lado dos 7 links e da pílula APOIO. A lista aberta e o
 * menu móvel mostram sempre o nome nativo completo.
 */
const LANGUAGES = [
    { code: 'pt', href: '/', native: 'Português' },
    { code: 'en', href: '/en/', native: 'English' },
    { code: 'uk', href: '/uk/', native: 'Українська' },
    { code: 'fr', href: '/fr/', native: 'Français' },
    { code: 'ro', href: '/ro/', native: 'Română' },
    { code: 'ar', href: '/ar/', native: 'العربية' },
    { code: 'de', href: '/de/', native: 'Deutsch' },
    { code: 'es', href: '/es/', native: 'Español' },
    { code: 'it', href: '/it/', native: 'Italiano' },
    { code: 'nl', href: '/nl/', native: 'Nederlands' },
    { code: 'ru', href: '/ru/', native: 'Русский' },
    { code: 'zh', href: '/zh/', native: '中文' },
    { code: 'bn', href: '/bn/', native: 'বাংলা' },
    { code: 'hi', href: '/hi/', native: 'हिन्दी' },
    { code: 'ur', href: '/ur/', native: 'اردو' },
]

// Mantém a página atual ao trocar de língua: /uk/direitos -> /de/direitos.
// Os slugs são iguais em todas as línguas de propósito (são rota, não texto).
function paginaAtual() {
    const base = import.meta.env.BASE_URL || '/'
    const p = window.location.pathname
    return p.startsWith(base) ? p.slice(base.length) : p.replace(/^\//, '')
}

export default function LanguageSelector({ variant = 'dropdown' }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const rootRef = useRef(null)

    useEffect(() => {
        if (!open) return
        const onOutside = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
        }
        const onEscape = (e) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', onOutside)
        document.addEventListener('keydown', onEscape)
        return () => {
            document.removeEventListener('mousedown', onOutside)
            document.removeEventListener('keydown', onEscape)
        }
    }, [open])

    const current = LANGUAGES.find((l) => l.code === LOCALE) ?? LANGUAGES[0]

    if (variant === 'inline') {
        // Lista horizontal simples (menu móvel, fundo claro).
        return (
            <nav aria-label={t('languageSelector.aria')} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pt-3">
                <Globe size={16} className="text-slate-500 shrink-0" aria-hidden="true" />
                {LANGUAGES.map((l) => (
                    <a
                        key={l.code}
                        href={l.href + paginaAtual()}
                        lang={l.code}
                        hrefLang={l.code}
                        translate="no"
                        aria-current={l.code === LOCALE ? 'true' : undefined}
                        className={`text-xs font-semibold py-2 transition-colors ${
                            l.code === LOCALE
                                ? 'text-brand-600 underline underline-offset-4'
                                : 'text-slate-600 hover:text-brand-600'
                        }`}
                    >
                        {l.native}
                    </a>
                ))}
            </nav>
        )
    }

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={t('languageSelector.aria')}
                className="flex items-center gap-1 px-1.5 py-1 text-sm font-bold text-slate-700 hover:text-brand-600 focus-visible:text-brand-600 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-600 rounded-md"
                title={current.native}
            >
                <Globe size={16} className="shrink-0" aria-hidden="true" />
                <span translate="no" className="uppercase tracking-wide">{current.code}</span>
                <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {open && (
                <ul
                    className="absolute end-0 mt-1 min-w-[10rem] rounded-xl bg-white border border-slate-200 shadow-lg overflow-hidden py-1 z-50 max-h-[70vh] overflow-y-auto"
                    aria-label={t('languageSelector.aria')}
                >
                    {LANGUAGES.map((l) => (
                        <li key={l.code}>
                            <a
                                href={l.href + paginaAtual()}
                                lang={l.code}
                                hrefLang={l.code}
                                translate="no"
                                aria-current={l.code === LOCALE ? 'true' : undefined}
                                className={`block px-4 py-2 text-sm transition-colors ${
                                    l.code === LOCALE
                                        ? 'font-bold text-brand-600 bg-brand-50'
                                        : 'font-medium text-slate-700 hover:bg-slate-50 hover:text-brand-600'
                                }`}
                            >
                                {l.native}
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
